import { ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, EventEmitter, HostListener, Input, Output, ViewChild, AfterViewChecked } from '@angular/core';
import { Router } from '@angular/router';
import { DomSanitizer, SafeHtml, SafeResourceUrl } from '@angular/platform-browser';
import { ChatAttachment, ChatMessage, ChatMessageSegment } from '../../models/chat.models';

export interface ActionButton {
  label: string;
  route: string;
}

/** Block produced after parsing a text segment for fenced chart/actions/title/suggestions blocks. */
export interface ParsedBlock {
  kind:
    | 'html'
    | 'chart'
    | 'actions'
    | 'suggestions'
    | 'product_card'
    | 'onboarding_guide'
    | 'report'
    | 'kpi_tiles'
    | 'csv'
    | 'loading';
  value: string;
  safeHtml?: SafeHtml;
}

@Component({
  selector: 'app-chat-message',
  templateUrl: './chat-message.component.html',
  styleUrls: ['./chat-message.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ChatMessageComponent implements AfterViewChecked {
  @Input({ required: true }) message!: ChatMessage;
  @Output() readonly suggestionPick = new EventEmitter<string>();
  @Output() readonly retry = new EventEmitter<{ text: string; attachments?: ChatAttachment[] }>();
  @ViewChild('msgBody') private msgBody?: ElementRef<HTMLElement>;
  private linksIntercepted = false;

  previewAttachment: ChatAttachment | null = null;

  constructor(
    private readonly router: Router,
    private readonly sanitizer: DomSanitizer,
    private readonly cdr: ChangeDetectorRef,
  ) {}

  @HostListener('document:keydown.escape')
  onEscape(): void {
    if (this.previewAttachment) this.closePreview();
  }

  /**
   * After each view update (streaming deltas produce new HTML), intercept clicks
   * on internal <a href="/..."> links to navigate via Angular Router instead of
   * triggering a full page reload.
   */
  ngAfterViewChecked(): void {
    if (!this.msgBody) return;
    // Re-bind when content changes (streaming adds new links)
    const links = this.msgBody.nativeElement.querySelectorAll<HTMLAnchorElement>('a[href^="/"]');
    if (links.length === 0) { this.linksIntercepted = false; return; }
    if (this.linksIntercepted) return;
    links.forEach((link) => {
      link.addEventListener('click', (e: Event) => {
        e.preventDefault();
        const href = link.getAttribute('href');
        if (href) this.navigateTo(href);
      });
    });
    this.linksIntercepted = true;
  }

  get isUser(): boolean { return this.message.role === 'user'; }
  get isAssistant(): boolean { return this.message.role === 'assistant'; }
  get isSystem(): boolean { return this.message.role === 'system' || !!this.message.isError; }

  get timestamp(): string {
    try {
      const d = new Date(this.message.createdAt);
      return `${d.getHours().toString().padStart(2, '0')}:${d.getMinutes().toString().padStart(2, '0')}`;
    } catch {
      return '';
    }
  }

  /** Returns segments if available, otherwise builds a single text segment from content. */
  get segments(): ChatMessageSegment[] {
    return this.message.segments?.length
      ? this.message.segments
      : [{ kind: 'text', content: this.message.content }];
  }

  /** Whether to show the streaming dots (no content yet, still streaming). */
  get showStreamingDots(): boolean {
    return !!this.message.isStreaming && !this.message.content;
  }

  trackByIndex(index: number): number { return index; }

  /**
   * Converts common markdown syntax to HTML so mixed markdown+HTML model output
   * renders cleanly. Only applied to text outside of HTML tags.
   * Handles: pipe tables, --- hr, ## h2, ### h3, **bold**, *italic*, `code`, newlines.
   */
  private markdownToHtml(text: string): string {
    // Pipe-tables BEFORE the split so they become a single inline run of <table>...</table>
    // tags that the rest of the pipeline will preserve as-is.
    text = this.markdownTablesToHtml(text);
    // Split on HTML block boundaries so we don't process inside <div>/<strong> etc.
    const parts = text.split(/(<[^>]+>)/);
    const converted = parts.map((part, i) => {
      // Odd-indexed parts are HTML tags — leave untouched
      if (i % 2 === 1) return part;
      return part
        // Horizontal rules (--- or ***) on their own line
        .replace(/^[ \t]*[-*]{3,}[ \t]*$/gm, '<hr style="border:none;border-top:1px solid #E5E7EB;margin:10px 0">')
        // ATX headers
        .replace(/^#### (.+)$/gm, '<h4 style="font-size:13px;font-weight:700;margin:8px 0 2px;color:#1A1A2E">$1</h4>')
        .replace(/^### (.+)$/gm, '<h3 style="font-size:14px;font-weight:700;margin:10px 0 4px;color:#1A1A2E">$1</h3>')
        .replace(/^## (.+)$/gm, '<h2 style="font-size:15px;font-weight:700;margin:12px 0 4px;color:#1A1A2E">$1</h2>')
        // Bold + italic combined
        .replace(/\*\*\*(.+?)\*\*\*/g, '<strong><em>$1</em></strong>')
        // Bold
        .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
        // Italic (single * not preceded/followed by another *)
        .replace(/(?<!\*)\*(?!\*)(.+?)(?<!\*)\*(?!\*)/g, '<em>$1</em>')
        // Inline code
        .replace(/`([^`]+)`/g, '<code style="background:#F3F4F6;padding:1px 5px;border-radius:4px;font-size:12px">$1</code>')
        // Double newline → paragraph break
        .replace(/\n\n/g, '<br><br>')
        // Single newline → line break
        .replace(/\n/g, '<br>');
    });
    return converted.join('');
  }

  /**
   * Detects markdown pipe tables and converts them to inline HTML tables.
   * A table is: header row | separator row (|:-:|:-:|) | one or more data rows,
   * each line wrapped in pipes. Output is a single line of <table>…</table>
   * with no internal newlines, so downstream `\n → <br>` conversion can't
   * break it.
   */
  private markdownTablesToHtml(text: string): string {
    if (text.indexOf('|') === -1) return text;
    const lines = text.split('\n');
    const out: string[] = [];
    let i = 0;
    const isRow = (l: string) => /^\s*\|.*\|\s*$/.test(l);
    const isSep = (l: string) => /^\s*\|(?:\s*:?-{2,}:?\s*\|)+\s*$/.test(l);
    const cells = (l: string) =>
      l.trim().replace(/^\|/, '').replace(/\|$/, '').split('|').map((c) => c.trim());
    while (i < lines.length) {
      if (i + 1 < lines.length && isRow(lines[i]) && isSep(lines[i + 1])) {
        const header = cells(lines[i]);
        let j = i + 2;
        const rows: string[][] = [];
        while (j < lines.length && isRow(lines[j])) {
          rows.push(cells(lines[j]));
          j++;
        }
        const headHtml = '<thead><tr>' + header.map((c) => `<th>${c}</th>`).join('') + '</tr></thead>';
        const bodyHtml = '<tbody>' + rows.map((r) => '<tr>' + r.map((c) => `<td>${c}</td>`).join('') + '</tr>').join('') + '</tbody>';
        out.push(`<table>${headHtml}${bodyHtml}</table>`);
        i = j;
      } else {
        out.push(lines[i]);
        i++;
      }
    }
    return out.join('\n');
  }

  /**
   * Parses a text segment's content into blocks: HTML (rendered via innerHTML),
   * fenced ```chart``` blocks, and fenced ```actions``` blocks.
   * Title blocks are consumed silently (handled by turn controller).
   * When isStreaming=true, incomplete fenced blocks are replaced with a loading placeholder.
   */
  parseTextBlocks(text: string, isStreaming = false): ParsedBlock[] {
    const out: ParsedBlock[] = [];
    // Allow optional whitespace/CR before the closing ``` so the block parses
    // even when the model omits the trailing newline or uses \r\n line endings.
    const re = /```(chart|actions|suggestions|product_card|onboarding_guide|report|kpi_tiles|csv|title)\s*[\r\n]+([\s\S]*?)[\r\n]*\s*```/g;
    let lastIndex = 0;
    let match: RegExpExecArray | null;
    while ((match = re.exec(text)) !== null) {
      if (match.index > lastIndex) {
        const raw = text.slice(lastIndex, match.index);
        const html = this.markdownToHtml(raw);
        out.push({ kind: 'html', value: html, safeHtml: this.sanitizer.bypassSecurityTrustHtml(html) });
      }
      const blockType = match[1] as
        | 'chart'
        | 'actions'
        | 'suggestions'
        | 'product_card'
        | 'onboarding_guide'
        | 'report'
        | 'kpi_tiles'
        | 'csv'
        | 'title';
      if (blockType !== 'title') {
        out.push({ kind: blockType, value: match[2] });
      }
      lastIndex = match.index + match[0].length;
    }
    if (lastIndex < text.length) {
      let raw = text.slice(lastIndex);
      if (isStreaming) {
        // If there's an unclosed fenced block being streamed, hide it and show loading instead
        const fenceIdx = raw.search(/```(chart|actions|suggestions|product_card|onboarding_guide|report|kpi_tiles|csv|title)/);
        if (fenceIdx >= 0) {
          const before = raw.slice(0, fenceIdx);
          if (before.trim()) {
            const html = this.markdownToHtml(before);
            out.push({ kind: 'html', value: html, safeHtml: this.sanitizer.bypassSecurityTrustHtml(html) });
          }
          out.push({ kind: 'loading', value: '' });
          return out;
        }
      }
      const html = this.markdownToHtml(raw);
      out.push({ kind: 'html', value: html, safeHtml: this.sanitizer.bypassSecurityTrustHtml(html) });
    }
    if (out.length === 0) {
      const html = this.markdownToHtml(text);
      out.push({ kind: 'html', value: html, safeHtml: this.sanitizer.bypassSecurityTrustHtml(html) });
    }
    return out;
  }

  /** Parse action buttons from JSON. */
  parseActions(json: string): ActionButton[] {
    try {
      const parsed = JSON.parse(json);
      return Array.isArray(parsed) ? parsed.filter((a: any) => a.label && a.route) : [];
    } catch {
      return [];
    }
  }

  /** Parse suggestion chips from JSON array. */
  parseSuggestions(json: string): string[] {
    try {
      const parsed = JSON.parse(json);
      return Array.isArray(parsed) ? parsed.filter((s: any) => typeof s === 'string' && s.length > 0).slice(0, 3) : [];
    } catch {
      return [];
    }
  }

  /**
   * Returns the first row (header) of a CSV block as a comma-separated preview string.
   * Used in the template to show the user what columns the CSV contains before download.
   */
  csvHeaderPreview(csv: string): string {
    const firstLine = csv.split(/\r?\n/, 1)[0] ?? '';
    return firstLine.length > 120 ? firstLine.slice(0, 117) + '…' : firstLine;
  }

  /** Number of data rows (excluding header) in a CSV block. */
  csvRowCount(csv: string): number {
    const lines = csv.split(/\r?\n/).filter((l) => l.trim().length > 0);
    return Math.max(0, lines.length - 1);
  }

  /**
   * Triggers a browser download for a CSV block. Builds a Blob from the inline
   * content (with UTF-8 BOM so Excel opens it without garbled accents) and
   * programmatically clicks an <a download> link.
   */
  downloadCsv(csv: string, filename = 'plantilla.csv'): void {
    const bom = '﻿';
    const blob = new Blob([bom + csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    setTimeout(() => URL.revokeObjectURL(url), 0);
  }

  /** Navigate programmatically — handles both /path and /path?key=val query params. */
  navigateTo(route: string): void {
    const [path, queryString] = route.split('?');
    const queryParams: Record<string, string> = {};
    if (queryString) {
      for (const pair of queryString.split('&')) {
        const [key, val] = pair.split('=');
        if (key) queryParams[decodeURIComponent(key)] = decodeURIComponent(val ?? '');
      }
    }
    this.router.navigate([path], { queryParams: Object.keys(queryParams).length > 0 ? queryParams : undefined });
  }

  // ── Attachment preview ─────────────────────────────────────────

  openPreview(att: ChatAttachment): void {
    if (att.mediaType === 'error') return;
    this.previewAttachment = att;
    this.cdr.markForCheck();
  }

  closePreview(): void {
    this.previewAttachment = null;
    this.cdr.markForCheck();
  }

  attachmentChipLabel(att: ChatAttachment): string {
    if (att.mediaType === 'error') return att.fileName;
    if (att.mediaType.startsWith('image/')) return `📷 ${att.fileName}`;
    if (att.mediaType === 'application/pdf') return `📄 ${att.fileName}`;
    if (att.mediaType === 'text/csv') return `📊 ${att.fileName}`;
    if (att.mediaType === 'text/plain' || att.textContent) return `📄 ${att.fileName}`;
    return `📎 ${att.fileName}`;
  }

  isImageAtt(att: ChatAttachment): boolean {
    return att.mediaType.startsWith('image/') && att.base64Data.length > 0;
  }

  isPdfAtt(att: ChatAttachment): boolean {
    return att.mediaType === 'application/pdf' && att.base64Data.length > 0;
  }

  isTextAtt(att: ChatAttachment): boolean {
    return !!att.textContent;
  }

  previewImgUrl(att: ChatAttachment): string {
    return `data:${att.mediaType};base64,${att.base64Data}`;
  }

  previewPdfUrl(att: ChatAttachment): SafeResourceUrl {
    return this.sanitizer.bypassSecurityTrustResourceUrl(`data:application/pdf;base64,${att.base64Data}`);
  }
}
